/**
 * Product catalog for the Order UI
 *
 * Products are fetched from the API at build time and stored in
 * products-catalog.json. The API serves products from the database,
 * which is synced from config/products.yaml on deploy.
 *
 * Build flow: products.yaml -> DB sync -> GET /api/products -> products-catalog.json
 */

import type { ProductGroup, ProductTier, CartItem, OrderTotals, Subject, ProductVariant } from "./types";
import productsCatalogRaw from "./products-catalog.json";
import { calculateDiscounts } from "./discounts";

// Type definition for the JSON catalog structure
interface CatalogTier {
  sku: string;
  tier_type: string;
  tier_order: number;
  // string when fetched from the API (pg numeric serialization), number when the
  // build fell back to config/products.json — accept both so fallback builds
  // still typecheck (deep-analysis fetch-products follow-up).
  unit_price_ex_gst_cents: string | number;
  name: string;
  description: string;
  uom: string;
  variant?: string;
  max_quantity?: number;
  min_quantity?: number;
  students_per_block?: number;
}

interface CatalogProductGroup {
  name: string;
  description: string;
  pricing_model: string;
  product_type?: string;
  channels?: string[];
  category_unspsc: string;
  manufacturer: string;
  link: string;
  subject: string;
  display_order?: number;
  tiers: CatalogTier[];
}

interface ProductCatalog {
  catalog_version: string;
  currency: string;
  gst_rate: number;
  product_groups: Record<string, CatalogProductGroup>;
}

export interface VariantCatalog {
  product_groups: Record<
    string,
    {
      tiers: Array<Pick<CatalogTier, "sku" | "tier_type" | "variant">>;
    }
  >;
}

/**
 * Build a product-key-to-variant lookup from raw catalog tiers.
 *
 * Only groups with at least two distinct variant values participate. A tier's
 * key is its SKU with the exact terminal `_${tier_type}` suffix removed.
 * Variant-less tiers and tiers with malformed suffixes are skipped. Conflicting
 * variant values for the same derived key make the catalog invalid.
 */
export function buildVariantKeyMap(rawCatalog: VariantCatalog): Map<string, string> {
  const variantKeyMap = new Map<string, string>();

  for (const group of Object.values(rawCatalog.product_groups)) {
    const variantValues = new Set(
      group.tiers.flatMap((tier) => (tier.variant ? [tier.variant] : [])),
    );
    if (variantValues.size < 2) continue;

    for (const tier of group.tiers) {
      if (!tier.variant) continue;

      const suffix = `_${tier.tier_type}`;
      if (!tier.sku.endsWith(suffix)) continue;

      const productKey = tier.sku.slice(0, -suffix.length);
      const existingVariant = variantKeyMap.get(productKey);
      if (existingVariant !== undefined && existingVariant !== tier.variant) {
        throw new Error(
          `Catalog conflict: key "${productKey}" derives from two tiers with conflicting variant values: "${existingVariant}" vs "${tier.variant}"`,
        );
      }

      variantKeyMap.set(productKey, tier.variant);
    }
  }

  return variantKeyMap;
}

const variantKeyMap = buildVariantKeyMap(productsCatalogRaw);

/**
 * Return the full variant ID associated with a catalog product key.
 * Single-variant products and unknown or malformed keys return `undefined`.
 */
export function getVariantForProductKey(productKey: string): string | undefined {
  return variantKeyMap.get(productKey);
}

// Cast the imported JSON to the proper type
const productsCatalog = productsCatalogRaw as ProductCatalog;

const GST_RATE = productsCatalog.gst_rate;

/**
 * Subject definitions in display order.
 * Every section starts collapsed; SubjectSection opens any section that
 * holds a cart item or an owned product.
 */
export const subjects: Subject[] = [
  {
    id: "hsc_mathematics",
    name: "HSC Mathematics – Teacher Edition",
    tagline: "STD1 | STD2 | ADV | EXT1 | EXT2",
    icon: "calculator",
    defaultExpanded: false,
  },
  {
    id: "nsw_mathematics",
    name: "NSW Mathematics (Y3–10) – Teacher Edition",
    tagline: "Complete Y3–10 mathematics resources for NSW schools",
    icon: "bar-chart",
    defaultExpanded: false,
  },
  {
    id: "vce_mathematics",
    name: "VCE",
    tagline: "General Mathematics | Mathematical Methods | Specialist Mathematics",
    icon: "calculator",
    defaultExpanded: false,
  },
  {
    id: "smartermaths_online",
    name: "SmarterMaths Online",
    tagline: "Adaptive testing and practice for student learning",
    icon: "laptop",
    defaultExpanded: false,
  },
  {
    id: "hsc_science",
    name: "HSC Science – Teacher Edition",
    tagline: "Biology | Chemistry | Physics | Engineering Studies | Health and Movement Science",
    icon: "flask",
    defaultExpanded: false,
  },
];

/**
 * Product name for on-screen rows: the catalog name without "Teacher Edition".
 * Display only — cart, checkout, invoices and UniMarket keep the full name.
 * Handles both "Advanced Mathematics Teacher Edition (Y11/12)" and
 * "NSW Mathematics (Y9-10) - Teacher Edition".
 */
export function displayProductName(name: string): string {
  return name.replace(/\s*[-–]?\s*Teacher Edition\b/g, "").replace(/\s{2,}/g, " ").trim();
}

/**
 * Convert YAML pricing model to typed format
 */
function getPricingModel(model: string): "tiered_per_user" | "flat" | "block_per_student" {
  switch (model) {
    case "tiered_per_user":
      return "tiered_per_user";
    case "block_per_student":
      return "block_per_student";
    default:
      return "flat";
  }
}

/**
 * Transform YAML product groups to UI format
 */
function transformYamlToProductGroups(): Record<string, ProductGroup> {
  const result: Record<string, ProductGroup> = {};

  for (const [groupId, group] of Object.entries(productsCatalog.product_groups)) {
    // Catalog keeps ext2 as hsc_ext2, but checkout/API expects hsc_ext2_slv.
    const checkoutGroupId = groupId === "hsc_ext2" ? "hsc_ext2_slv" : groupId;

    // Check if this product has variants (multiple tiers with different variant values)
    const variantValues = new Set(
      group.tiers.filter((t) => t.variant).map((t) => t.variant)
    );

    if (variantValues.size > 1) {
      // Multi-variant product (e.g., bronze/silver)
      const variants: ProductVariant[] = [];

      for (const variantId of variantValues) {
        const variantTiers = group.tiers.filter((t) => t.variant === variantId);
        variants.push({
          id: variantId!,
          name: variantId!.charAt(0).toUpperCase() + variantId!.slice(1),
          tiers: variantTiers.map(transformTier),
        });
      }

      // Check for shared additional tier (no variant specified)
      const sharedAdditional = group.tiers.find(
        (t) => !t.variant && t.tier_type === "additional"
      );
      if (sharedAdditional) {
        // Add shared additional to each variant
        for (const variant of variants) {
          variant.tiers.push(transformTier(sharedAdditional));
        }
      }

      // Sort variants: bronze first, then silver
      variants.sort((a, b) => {
        const order = ["bronze", "silver"];
        return order.indexOf(a.id) - order.indexOf(b.id);
      });

      result[checkoutGroupId] = {
        id: checkoutGroupId,
        name: group.name,
        description: group.description,
        pricingModel: getPricingModel(group.pricing_model),
        productType: (group.product_type || "teacher") as "teacher" | "student",
        subject: group.subject,
        displayOrder: group.display_order,
        channels: group.channels || ["unimarket", "direct"],
        defaultVariant: "silver",
        variants,
      };
    } else {
      // Single-variant product (or no variants)
      result[checkoutGroupId] = {
        id: checkoutGroupId,
        name: group.name,
        description: group.description,
        pricingModel: getPricingModel(group.pricing_model),
        productType: (group.product_type || "teacher") as "teacher" | "student",
        subject: group.subject,
        displayOrder: group.display_order,
        channels: group.channels || ["unimarket", "direct"],
        tiers: group.tiers.map(transformTier),
      };
    }
  }

  return result;
}

/**
 * Transform a YAML tier to UI tier format
 */
function transformTier(tier: CatalogTier): ProductTier {
  return {
    sku: tier.sku,
    tierType: tier.tier_type as "initial" | "additional",
    tierOrder: tier.tier_order,
    maxQuantity: tier.max_quantity,
    minQuantity: tier.min_quantity,
    unitPriceExGstCents: typeof tier.unit_price_ex_gst_cents === "number"
      ? tier.unit_price_ex_gst_cents
      : parseInt(tier.unit_price_ex_gst_cents, 10),
    name: tier.name,
    description: tier.description,
    uom: tier.uom,
    studentsPerBlock: tier.students_per_block,
  };
}

/**
 * Product catalog (transformed from YAML)
 */
export const productGroups: Record<string, ProductGroup> = transformYamlToProductGroups();

/**
 * Catalog version for cache validation
 */
export const catalogVersion: string = productsCatalog.catalog_version;

/**
 * Get products grouped by subject
 */
export function getProductsBySubject(channel?: string): Map<Subject, ProductGroup[]> {
  const result = new Map<Subject, ProductGroup[]>();

  for (const subject of subjects) {
    const products = Object.values(productGroups)
      .filter((p) => p.subject === subject.id)
      .filter((p) => !channel || (p.channels || ["unimarket", "direct"]).includes(channel))
      .sort((a, b) => {
        const orderA = a.displayOrder ?? 999;
        const orderB = b.displayOrder ?? 999;
        if (orderA !== orderB) return orderA - orderB;
        return a.id.localeCompare(b.id);
      });
    if (products.length > 0) {
      result.set(subject, products);
    }
  }

  return result;
}

/**
 * Direct order page (/) regions. Region is a DISPLAY filter only: it never edits
 * the cart and never restricts which school can check out.
 */
export type OrderRegion = "nsw_act" | "vic" | "other";
export type NswActSchoolType = "non_government" | "government";

export const ORDER_REGIONS: readonly OrderRegion[] = ["nsw_act", "vic", "other"];
export const NSW_ACT_SCHOOL_TYPES: readonly NswActSchoolType[] = ["non_government", "government"];
export const DEFAULT_ORDER_REGION: OrderRegion = "nsw_act";
export const DEFAULT_NSW_ACT_SCHOOL_TYPE: NswActSchoolType = "non_government";

/** Subject ids each region shows. Display order always comes from `subjects`. */
const REGION_SUBJECT_IDS: Record<OrderRegion, readonly string[]> = {
  nsw_act: ["hsc_mathematics", "nsw_mathematics", "smartermaths_online", "hsc_science"],
  vic: ["vce_mathematics", "smartermaths_online"],
  other: ["hsc_mathematics", "nsw_mathematics", "vce_mathematics", "smartermaths_online", "hsc_science"],
};

/**
 * Per-region product allow-list inside a subject (absent = every product).
 * VIC sees SmarterMaths Online Years 3–10 only: `smo_y3_9` is the legacy id of the
 * product now named Years 3–10; HSC Minimum Standards (`smo_minstd`) is hidden.
 */
const REGION_PRODUCT_IDS: Partial<Record<OrderRegion, Record<string, readonly string[]>>> = {
  vic: { smartermaths_online: ["smo_y3_9"] },
};

/**
 * True when the choice routes the school to NSW DoE EdBuy instead of this page's
 * picker and cart. NSW and ACT share the one government/non-government rule.
 */
export function isEdBuyRouted(region: OrderRegion, schoolType: NswActSchoolType): boolean {
  return region === "nsw_act" && schoolType === "government";
}

/**
 * Direct-channel products for a region, grouped by subject in display order.
 * NSW & ACT government schools order through EdBuy, so they get no products.
 * `schoolType` only matters for NSW & ACT.
 */
export function getProductsForRegion(
  region: OrderRegion,
  schoolType: NswActSchoolType = DEFAULT_NSW_ACT_SCHOOL_TYPE,
): Map<Subject, ProductGroup[]> {
  const result = new Map<Subject, ProductGroup[]>();
  if (isEdBuyRouted(region, schoolType)) return result;

  const subjectIds = REGION_SUBJECT_IDS[region];
  const productIds = REGION_PRODUCT_IDS[region] ?? {};

  for (const [subject, products] of getProductsBySubject("direct")) {
    if (!subjectIds.includes(subject.id)) continue;

    const allowed = productIds[subject.id];
    const visible = allowed ? products.filter((p) => allowed.includes(p.id)) : products;
    if (visible.length > 0) {
      result.set(subject, visible);
    }
  }

  return result;
}

/**
 * Get the tiers for a product, considering variant selection
 */
export function getProductTiers(group: ProductGroup, variantId?: string): ProductTier[] {
  if (group.variants && group.variants.length > 0) {
    const selectedVariant = variantId || group.defaultVariant;
    const variant = group.variants.find((v) => v.id === selectedVariant);
    return variant?.tiers || group.variants[0].tiers;
  }
  return group.tiers || [];
}

/**
 * Build cart items from a product group, quantity, and optional variant.
 * Handles tiered pricing (initial + additional pattern).
 *
 * @param isAddingToExisting - If true, skip initial tier (for existing subscriptions adding more seats)
 */
export function buildCartItems(
  group: ProductGroup,
  quantity: number,
  variantId?: string,
  isAddingToExisting?: boolean
): CartItem[] {
  if (quantity < 1) return [];

  const tiers = getProductTiers(group, variantId);
  const items: CartItem[] = [];
  const sortedTiers = [...tiers].sort((a, b) => a.tierOrder - b.tierOrder);

  if (group.pricingModel === "flat") {
    const tier = sortedTiers[0];
    if (tier) {
      items.push({
        supplierPartId: tier.sku,
        name: tier.name,
        description: tier.description,
        unitPriceExGstCents: tier.unitPriceExGstCents,
        quantity,
        uom: tier.uom,
      });
    }
  } else if (group.pricingModel === "tiered_per_user") {
    const initialTier = sortedTiers.find((t) => t.tierType === "initial");
    const additionalTier = sortedTiers.find((t) => t.tierType === "additional");

    // Only add initial tier for NEW subscriptions (not when adding to existing)
    if (initialTier && !isAddingToExisting) {
      items.push({
        supplierPartId: initialTier.sku,
        name: initialTier.name,
        description: initialTier.description,
        unitPriceExGstCents: initialTier.unitPriceExGstCents,
        quantity: 1,
        uom: initialTier.uom,
      });
    }

    // For existing subscriptions: ALL quantity is additional teachers
    // For new subscriptions: quantity - 1 is additional teachers
    const additionalQty = isAddingToExisting ? quantity : Math.max(0, quantity - 1);

    if (additionalTier && additionalQty > 0) {
      items.push({
        supplierPartId: additionalTier.sku,
        name: additionalTier.name,
        description: additionalTier.description,
        unitPriceExGstCents: additionalTier.unitPriceExGstCents,
        quantity: additionalQty,
        uom: additionalTier.uom,
      });
    }
  } else if (group.pricingModel === "block_per_student") {
    // Student block products (e.g., SMO): same tier logic as tiered_per_user
    // Initial block (required for new subscriptions) + additional blocks
    const initialTier = sortedTiers.find((t) => t.tierType === "initial");
    const additionalTier = sortedTiers.find((t) => t.tierType === "additional");

    // Only add initial tier for NEW subscriptions (not when adding to existing)
    if (initialTier && !isAddingToExisting) {
      items.push({
        supplierPartId: initialTier.sku,
        name: initialTier.name,
        description: initialTier.description,
        unitPriceExGstCents: initialTier.unitPriceExGstCents,
        quantity: 1,
        uom: initialTier.uom,
        studentsPerBlock: initialTier.studentsPerBlock,
      });
    }

    // For existing subscriptions: ALL quantity is additional blocks
    // For new subscriptions: quantity - 1 is additional blocks
    const additionalQty = isAddingToExisting ? quantity : Math.max(0, quantity - 1);

    if (additionalTier && additionalQty > 0) {
      items.push({
        supplierPartId: additionalTier.sku,
        name: additionalTier.name,
        description: additionalTier.description,
        unitPriceExGstCents: additionalTier.unitPriceExGstCents,
        quantity: additionalQty,
        uom: additionalTier.uom,
        studentsPerBlock: additionalTier.studentsPerBlock,
      });
    }
  }

  return items;
}

/**
 * Cart entry data needed for building cart items
 */
export interface CartEntryInput {
  group: ProductGroup;
  quantity: number;
  variantId?: string;
  isAddingToExisting?: boolean;
}

/**
 * Build cart items with automatic bundle discounts.
 * This function builds all regular cart items and then calculates any
 * applicable bundle discounts, returning them as additional line items
 * with negative prices.
 */
export function buildCartItemsWithDiscounts(entries: CartEntryInput[]): CartItem[] {
  // Build regular cart items
  const regularItems: CartItem[] = [];
  for (const entry of entries) {
    const items = buildCartItems(
      entry.group,
      entry.quantity,
      entry.variantId,
      entry.isAddingToExisting
    );
    regularItems.push(...items);
  }

  // Calculate and append discount line items
  const discounts = calculateDiscounts(regularItems);

  return [...regularItems, ...discounts];
}

/**
 * Calculate order totals from cart items.
 * Handles negative prices (discounts) and ensures subtotal doesn't go below 0.
 */
export function calculateTotals(items: CartItem[]): OrderTotals {
  const rawSubtotalExGstCents = items.reduce(
    (sum, item) => sum + item.unitPriceExGstCents * item.quantity,
    0
  );
  // Clamp subtotal to minimum 0 (in case discounts exceed product prices)
  const subtotalExGstCents = Math.max(0, rawSubtotalExGstCents);
  const gstCents = Math.round(subtotalExGstCents * GST_RATE);
  const totalIncGstCents = subtotalExGstCents + gstCents;

  return { subtotalExGstCents, gstCents, ccSurchargeCents: 0, totalIncGstCents };
}

/**
 * Format cents as a dollar string
 */
export function formatCents(cents: number): string {
  const amount = Math.abs(cents / 100).toFixed(2);
  return cents < 0 ? `-$${amount}` : `$${amount}`;
}

/**
 * Format cents as whole dollars (no decimals) for breakdown text
 */
export function formatWholeDollars(cents: number): string {
  return `$${Math.round(cents / 100)}`;
}

/**
 * Parse quantity from existing cart items (for edit mode)
 */
export function parseQuantityFromCart(cart: CartItem[]): number {
  if (cart.length === 0) return 1;

  // Find initial and additional items
  const initialItem = cart.find((item) => item.supplierPartId.includes("_initial"));
  const additionalItem = cart.find((item) => item.supplierPartId.includes("_additional"));

  const initialQty = initialItem?.quantity || 0;
  const additionalQty = additionalItem?.quantity || 0;

  return initialQty + additionalQty || 1;
}

/**
 * Calculate total students from cart items (for student block products)
 * Returns 0 for non-student products
 */
export function calculateTotalStudents(items: CartItem[]): number {
  return items.reduce((total, item) => {
    return total + (item.studentsPerBlock || 0) * item.quantity;
  }, 0);
}

/**
 * Calculate the line total (ex-GST) for a cart entry.
 * This combines initial + additional tier pricing into a single total.
 */
export function calculateEntryTotal(
  group: ProductGroup,
  quantity: number,
  variantId?: string,
  isAddingToExisting?: boolean
): number {
  const items = buildCartItems(group, quantity, variantId, isAddingToExisting);
  return items.reduce((sum, item) => sum + item.unitPriceExGstCents * item.quantity, 0);
}

/**
 * Check if a product group is a student product (vs teacher product)
 */
export function isStudentProduct(group: ProductGroup): boolean {
  return group.productType === "student";
}

/**
 * VCE school licences: the three VCE courses are ordinary `teacher` products on
 * the `tiered_per_user` model, sold as ONE fixed-price licence per course. The
 * customer sees "School licence"; the cart entry, checkout request and quote item
 * all carry quantity 99 — the existing "total teacher seats" meaning — which
 * buildCartItems splits into initial x1 ($370) + additional x98 ($0).
 *
 * Explicit allow-list: never infer a licence from a free additional tier.
 * Copies of order-api shared/vce-school-licence.ts, pinned by its
 * test/unit/duplication-drift.test.ts.
 */
export const VCE_SCHOOL_LICENCE_PRODUCT_IDS: readonly string[] = ["vce_gen_slv", "vce_methods_slv", "vce_spec_slv"];

/** Teacher seats included in one VCE school licence (1 paid + 98 at $0). */
export const VCE_SCHOOL_LICENCE_QUANTITY = 99;

export function isVceSchoolLicence(group: Pick<ProductGroup, "id">): boolean {
  return VCE_SCHOOL_LICENCE_PRODUCT_IDS.includes(group.id);
}

/**
 * Check if a product is an NSW Stage Mathematics product (nsw_stg2, nsw_stg3, nsw_stg4, nsw_stg5)
 */
export function isNswStageProduct(group: ProductGroup): boolean {
  return group.id.startsWith("nsw_stg");
}
